import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? API_ROOT.endsWith("/api")
    ? API_ROOT
    : `${API_ROOT}/api`
  : "/api";

const linkBtn = {
  cursor: "pointer",
  textDecoration: "underline",
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
};

const ALLOWED_MODULES = [4, 10, 26];
const ALLOWED_TEACHER_DESIGNATION_IDS = new Set([1, 4, 5, 28, 29, 31, 37]);

const EMPTY_FORM = {
  NameTitleID: "",
  MemberName: "",
  DesignationID: "",
  FNameTitleID: "",
  FName: "",
  Gender: "M",
  Country: "91",
  Mobile: "",
  DOB: "",
  EMail: "",
  PermanentAddress: "",
  PermanentPIN: "",
  PermanentStateID: "",
  PermanentCityID: "",
  CollegeID: "",
  ACollege: "",
  IDCardNo: ""
  ,ValidityDate: "",
  EmpType: "",
  SupportingDoc: ""
};

function toTitleCase(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trimStart()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

export default function CollegeTeachersList() {
  const navigate = useNavigate();
  const localUser = JSON.parse(localStorage.getItem("user") || "null");
  const moduleId = parseInt(localUser?.moduleId, 10);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [filterName, setFilterName] = useState("");
  const [filterCollege, setFilterCollege] = useState("");
  const [filterDesignation, setFilterDesignation] = useState("");
  const [filterEmpType, setFilterEmpType] = useState("");
  const [filterState, setFilterState] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docMsg, setDocMsg] = useState({ text: "", type: "" });
  const [form, setForm] = useState(EMPTY_FORM);

  const [titles, setTitles] = useState([]);
  const [fatherTitles, setFatherTitles] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [designations, setDesignations] = useState([]);

  const todayMaxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().slice(0, 10);
  }, []);

  const validityDateTooltip =
    form.EmpType === "P"
      ? "Date of Retirement for Permanent Employee"
      : form.EmpType === "C"
        ? "Term of Appointment for Contractual Teacher"
        : "Date of Retirement for Permanent Employee / Term of Appointment for Contractual Teacher";

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) {
      navigate("/");
      return;
    }

    const fetchRows = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(
          `${API_BASE}/guesthouse/college-teachers-list?moduleId=${encodeURIComponent(moduleId)}`
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || "Failed to fetch college teachers list");
        }
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        setRows([]);
        setError(err.message || "Failed to fetch college teachers list");
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
    loadMasters();
  }, [moduleId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRows = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(
        `${API_BASE}/guesthouse/college-teachers-list?moduleId=${encodeURIComponent(moduleId)}`
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Failed to fetch college teachers list");
      }
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch college teachers list");
    } finally {
      setLoading(false);
    }
  };

  const loadMasters = async (stateId = "") => {
    try {
      const [titlesRes, statesRes, collegesRes, designationsRes] = await Promise.all([
        fetch(`${API_BASE}/meta/name-titles`),
        fetch(`${API_BASE}/states`),
        fetch(`${API_BASE}/meta/colleges`),
        fetch(`${API_BASE}/meta/designations`)
      ]);

      const [titlesData, statesData, collegesData, designationsData] = await Promise.all([
        titlesRes.json(),
        statesRes.json(),
        collegesRes.json(),
        designationsRes.json()
      ]);

      const allTitles = Array.isArray(titlesData) ? titlesData : [];
      setTitles(allTitles.filter((item) => Number(item.NameTitleID) !== 7));
      setFatherTitles(allTitles);
      setStates(Array.isArray(statesData) ? statesData : []);
      setColleges(Array.isArray(collegesData) ? collegesData : []);
      setDesignations(
        (Array.isArray(designationsData) ? designationsData : []).filter((item) =>
          ALLOWED_TEACHER_DESIGNATION_IDS.has(Number(item.DesignationID))
        )
      );

      if (stateId) {
        const cityRes = await fetch(`${API_BASE}/cities?stateId=${encodeURIComponent(stateId)}`);
        const cityData = await cityRes.json();
        setCities(Array.isArray(cityData) ? cityData : []);
      } else {
        setCities([]);
      }
    } catch {
      setTitles([]);
      setFatherTitles([]);
      setStates([]);
      setColleges([]);
      setDesignations([]);
      setCities([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["Mobile", "PermanentPIN", "Country"].includes(name)) {
      if (!/^\d*$/.test(value)) return;
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (["MemberName", "FName"].includes(name)) {
      setForm((prev) => ({ ...prev, [name]: toTitleCase(value) }));
      return;
    }

    if (name === "EMail") {
      setForm((prev) => ({ ...prev, [name]: value.toLowerCase().trim() }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStateChange = async (stateId) => {
    setForm((prev) => ({ ...prev, PermanentStateID: stateId, PermanentCityID: "" }));
    if (!stateId) {
      setCities([]);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/cities?stateId=${encodeURIComponent(stateId)}`);
      const data = await res.json();
      setCities(Array.isArray(data) ? data : []);
    } catch {
      setCities([]);
    }
  };

  const openEditModal = async (teacherId) => {
    try {
      setFormError("");
      setSuccessMsg("");

      const detailRes = await fetch(
        `${API_BASE}/guesthouse/college-teachers-list/${encodeURIComponent(teacherId)}?moduleId=${encodeURIComponent(moduleId)}`
      );
      const detailData = await detailRes.json();
      if (!detailRes.ok) {
        throw new Error(detailData?.message || "Failed to fetch college teacher detail");
      }

      setEditingTeacherId(teacherId);
      setForm({
        NameTitleID: String(detailData.NameTitleID || ""),
        MemberName: detailData.MemberName || "",
        DesignationID: String(detailData.DesignationID || ""),
        FNameTitleID: String(detailData.FNameTitleID || ""),
        FName: detailData.FName || "",
        Gender: detailData.Gender || "M",
        Country: detailData.Country || "91",
        Mobile: detailData.Mobile || "",
        DOB: detailData.DOB ? String(detailData.DOB).slice(0, 10) : "",
        EMail: detailData.EMail || "",
        PermanentAddress: detailData.PermanentAddress || "",
        PermanentPIN: detailData.PermanentPIN || "",
        PermanentStateID: String(detailData.PermanentStateID || ""),
        PermanentCityID: String(detailData.PermanentCityID || ""),
        CollegeID: String(detailData.CollegeID || ""),
        ACollege: detailData.ACollege || "",
        IDCardNo: detailData.IDCardNo || "",
        ValidityDate: detailData.ValidityDate ? String(detailData.ValidityDate).slice(0, 10) : "",
        EmpType: detailData.EmpType || "",
        SupportingDoc: detailData.SupportingDoc || ""
      });
      setDocFile(null);
      setDocMsg({ text: "", type: "" });

      await loadMasters(String(detailData.PermanentStateID || ""));
      setShowModal(true);
    } catch (err) {
      setFormError(err.message || "Failed to load college teacher detail");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const required = [
      "NameTitleID",
      "MemberName",
      "DesignationID",
      "FNameTitleID",
      "FName",
      "Gender",
      "Country",
      "Mobile",
      "DOB",
      "EMail",
      "PermanentAddress",
      "PermanentPIN",
      "PermanentStateID",
      "PermanentCityID",
      "CollegeID",
      "IDCardNo"
    ];

    for (const field of required) {
      if (!String(form[field] || "").trim()) {
        setFormError("All mandatory fields must be filled");
        return;
      }
    }

    const mobile = String(form.Mobile || "").replace(/^0+/, "");
    if (!/^\d{10}$/.test(mobile)) {
      setFormError("Mobile must be exactly 10 digits");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.EMail || "")) {
      setFormError("Invalid email format.");
      return;
    }

    if (!/^\d{6}$/.test(form.PermanentPIN || "")) {
      setFormError("PIN must be exactly 6 digits");
      return;
    }

    if (!["M", "F", "O"].includes(String(form.Gender || "").toUpperCase())) {
      setFormError("Invalid gender value.");
      return;
    }

    const dob = new Date(form.DOB);
    if (Number.isNaN(dob.getTime())) {
      setFormError("Invalid DOB");
      return;
    }

    try {
      setSaving(true);
      setFormError("");
      const res = await fetch(
        `${API_BASE}/guesthouse/college-teachers-list/${encodeURIComponent(editingTeacherId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, Mobile: mobile, moduleId })
        }
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Update failed");
      }

      setShowModal(false);
      setEditingTeacherId(null);
      setSuccessMsg(data?.message || "College teacher updated successfully.");
      await loadRows();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setFormError(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDocUpload = async () => {
    if (!docFile) { setDocMsg({ text: "Please select a file first.", type: "danger" }); return; }
    const allowed = ["image/png", "image/jpg", "image/jpeg"];
    if (!allowed.includes(docFile.type)) { setDocMsg({ text: "Only JPG, JPEG, PNG files are allowed.", type: "danger" }); return; }
    setDocUploading(true);
    setDocMsg({ text: "", type: "" });
    try {
      const fd = new FormData();
      fd.append("SupportDoc", docFile);
      const res = await fetch(
        `${API_BASE}/college-teachers/upload-doc/${editingTeacherId}?moduleId=${moduleId}`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Upload failed");
      setDocMsg({ text: data.message || "Document uploaded.", type: "success" });
      setForm(prev => ({ ...prev, SupportingDoc: `uploads/cteacher_idcard/${editingTeacherId}.jpg` }));
      setDocFile(null);
    } catch (err) {
      setDocMsg({ text: err.message, type: "danger" });
    } finally {
      setDocUploading(false);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (filterName && !String(row.TName || "").toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterCollege && String(row.CollegeID || "") !== filterCollege) return false;
      if (filterDesignation && String(row.DesignationID || "") !== filterDesignation) return false;
      if (filterEmpType && (row.EmpType || "") !== filterEmpType) return false;
      if (filterState && String(row.PermanentStateID || "") !== filterState) return false;
      return true;
    });
  }, [rows, filterName, filterCollege, filterDesignation, filterEmpType, filterState]);

  if (!ALLOWED_MODULES.includes(moduleId)) return null;

  return (
    <section className="container-fluid">
      <h3 className="mb-3">Teachers of Affiliated Colleges</h3>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {formError && !showModal && <div className="alert alert-danger">{formError}</div>}

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
        <div className="row g-2 mb-3 align-items-end">
          <div className="col-sm-3">
            <label className="form-label mb-1 small fw-semibold">Search by Name</label>
            <input
              className="form-control form-control-sm"
              placeholder="Type name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
          </div>
          <div className="col-sm-3">
            <label className="form-label mb-1 small fw-semibold">College</label>
            <select className="form-select form-select-sm" value={filterCollege} onChange={(e) => setFilterCollege(e.target.value)}>
              <option value="">All Colleges</option>
              {colleges.map((c) => <option key={c.CollegeID} value={String(c.CollegeID)}>{c.CollegeDisplay}</option>)}
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1 small fw-semibold">Designation</label>
            <select className="form-select form-select-sm" value={filterDesignation} onChange={(e) => setFilterDesignation(e.target.value)}>
              <option value="">All Designations</option>
              {designations.map((d) => <option key={d.DesignationID} value={String(d.DesignationID)}>{d.Designation}</option>)}
            </select>
          </div>
          <div className="col-sm-1">
            <label className="form-label mb-1 small fw-semibold">Emp Type</label>
            <select className="form-select form-select-sm" value={filterEmpType} onChange={(e) => setFilterEmpType(e.target.value)}>
              <option value="">All</option>
              <option value="P">Permanent</option>
              <option value="C">Contractual</option>
              <option value="R">Retired</option>
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1 small fw-semibold">State</label>
            <select className="form-select form-select-sm" value={filterState} onChange={(e) => setFilterState(e.target.value)}>
              <option value="">All States</option>
              {states.map((s) => <option key={s.StateID} value={String(s.StateID)}>{s.State}</option>)}
            </select>
          </div>
          <div className="col-sm-auto">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setFilterName(""); setFilterCollege(""); setFilterDesignation(""); setFilterEmpType(""); setFilterState(""); }}
            >
              Clear
            </button>
          </div>
        </div>
        <div className="text-muted small mb-2">
          Showing {filteredRows.length} of {rows.length} records
        </div>
        <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
          <table className="table table-bordered table-striped table-hover table-sm align-middle" style={{ '--bs-table-hover-bg': '#aed6f1' }}>
            <thead style={{ '--bs-table-bg': '#008080', color: 'white', position: 'sticky', top: 0, zIndex: 4 }}>
              <tr>
                <th>SNo</th>
                <th>Name</th>
                <th>Designation</th>
                <th>Father'sName</th>
                <th>Mobile</th>
                <th>DOB</th>
                <th>College</th>
                <th>Validity</th>
                <th>EmpType</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Doc</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center text-muted">
                    No records found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => (
                  <tr key={row.TeacherID || index}>
                    <td>{index + 1}</td>
                    <td>{[row.NameTitle, row.TName].filter(Boolean).join(" ")}</td>
                    <td>{row.Designation || ""}</td>
                    <td>{[row.FNameTitle, row.FName].filter(Boolean).join(" ")}</td>
                    <td>{row.Mobile || ""}</td>
                    <td>{formatDate(row.DOB)}</td>
                    <td>{row.ACollege || ""}</td>
                    <td>{formatDate(row.ValidityDate)}</td>
                    <td>{row.EmpType === "P" ? "Permanent" : row.EmpType === "C" ? "Contractual" : row.EmpType === "R" ? "Retired" : row.EmpType || "-"}</td>
                    <td>{row.PermanentAddress || ""}</td>
                    <td>{row.City || ""}</td>
                    <td>{row.State || ""}</td>
                    <td>
                      {row.SupportingDoc
                        ? <button type="button" style={{ ...linkBtn, color: "#0dcaf0" }} onClick={() => window.open(`${API_BASE}/college-teachers/doc/${row.TeacherID}`, "_blank")}>View</button>
                        : <span className="text-muted">-</span>}
                    </td>
                    <td>
                      <button
                        type="button"
                        style={{ ...linkBtn, color: "#997404" }}
                        onClick={() => openEditModal(row.TeacherID)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg" style={{ maxWidth: "960px" }}>
            <div className="modal-content" style={{ backgroundColor: "#e8e8e8" }}>
              <div className="modal-header">
                <h5 className="modal-title">Edit College Teacher</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTeacherId(null);
                    setForm(EMPTY_FORM);
                    setFormError("");
                  }}
                />
              </div>

              <form onSubmit={handleUpdate}>
                <div className="modal-body">
                  {formError && <div className="alert alert-danger">{formError}</div>}

                  <div className="row g-2">
                    {/* ── Line 1 ── */}
                    <div className="col-sm-1">
                      <label className="form-label">Title</label>
                      <select className="form-control" name="NameTitleID" value={form.NameTitleID} onChange={handleChange} required>
                        <option value="">Select</option>
                        {titles.map((t) => (
                          <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-3">
                      <label className="form-label">Name</label>
                      <input className="form-control" name="MemberName" value={form.MemberName} readOnly />
                    </div>

                    <div className="col-sm-1">
                      <label className="form-label">Gender</label>
                      <select className="form-control" name="Gender" value={form.Gender} onChange={handleChange} required>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Others</option>
                      </select>
                    </div>

                    <div className="col-sm-4">
                      <label className="form-label">Designation</label>
                      <select className="form-control" name="DesignationID" value={form.DesignationID} onChange={handleChange} required>
                        <option value="">Select Designation</option>
                        {designations.map((designation) => (
                          <option key={designation.DesignationID} value={designation.DesignationID}>{designation.Designation}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">Employee Type</label>
                      <select className="form-control" name="EmpType" value={form.EmpType} onChange={handleChange}>
                        <option value="">Select</option>
                        <option value="C">Contractual</option>
                        <option value="P">Permanent</option>
                        <option value="R">Retired</option>
                      </select>
                    </div>

                    <div className="col-12"></div>{/* force line break */}

                    {/* ── Line 2 ── */}
                    <div className="col-sm-1">
                      <label className="form-label">F.Title</label>
                      <select className="form-control" name="FNameTitleID" value={form.FNameTitleID} onChange={handleChange} required>
                        <option value="">Select</option>
                        {fatherTitles.map((t) => (
                          <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-3">
                      <label className="form-label">Father Name</label>
                      <input className="form-control" name="FName" value={form.FName} onChange={handleChange} required />
                    </div>

                    <div className="col-sm-1">
                      <label className="form-label">Country</label>
                      <input className="form-control" name="Country" value={form.Country} onChange={handleChange} required />
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">Mobile</label>
                      <input className="form-control" name="Mobile" value={form.Mobile} readOnly />
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">DOB</label>
                      <input className="form-control" type="date" name="DOB" value={form.DOB} onChange={handleChange} max={todayMaxDob} required />
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label" title={validityDateTooltip}>
                        Valid Till
                        <i className="bi bi-info-circle ms-1 text-muted" style={{ fontSize: "0.8rem" }} title={validityDateTooltip}></i>
                      </label>
                      <input className="form-control" type="date" name="ValidityDate" value={form.ValidityDate} onChange={handleChange} />
                    </div>

                    <div className="col-sm-5">
                      <label className="form-label">Permanent Address</label>
                      <input className="form-control" name="PermanentAddress" value={form.PermanentAddress} onChange={handleChange} required />
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">PIN</label>
                      <input className="form-control" name="PermanentPIN" value={form.PermanentPIN} onChange={handleChange} maxLength={6} required />
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">State</label>
                      <select
                        className="form-control"
                        name="PermanentStateID"
                        value={form.PermanentStateID}
                        onChange={(e) => handleStateChange(e.target.value)}
                        required
                      >
                        <option value="">Select State</option>
                        {states.map((s) => (
                          <option key={s.StateID} value={s.StateID}>{s.State}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">City</label>
                      <select className="form-control" name="PermanentCityID" value={form.PermanentCityID} onChange={handleChange} required>
                        <option value="">Select City</option>
                        {cities.map((c) => (
                          <option key={c.CityID} value={c.CityID}>{c.City}</option>
                        ))}
                      </select>
                    </div>

                    {/* ── EMail | IDCard | SupportingDoc ── */}
                    <div className="col-sm-4">
                      <label className="form-label">EMail</label>
                      <input className="form-control" type="email" name="EMail" value={form.EMail} onChange={handleChange} required />
                    </div>

                    <div className="col-sm-2">
                      <label className="form-label">Identity Card No.</label>
                      <input className="form-control" name="IDCardNo" value={form.IDCardNo} onChange={handleChange} required />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label">Supporting Doc</label>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {form.SupportingDoc
                          ? <button type="button" className="btn btn-sm btn-outline-info" onClick={() => window.open(`${API_BASE}/college-teachers/doc/${editingTeacherId}`, "_blank")}>View</button>
                          : <span className="text-muted small">No doc</span>}
                        <input
                          type="file"
                          className="form-control form-control-sm"
                          style={{ maxWidth: "220px" }}
                          accept=".png,.jpg,.jpeg"
                          onChange={(e) => { setDocFile(e.target.files?.[0] || null); setDocMsg({ text: "", type: "" }); }}
                        />
                        <button type="button" className="btn btn-sm btn-success" onClick={handleDocUpload} disabled={docUploading}>
                          {docUploading ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                      {docMsg.text && <div className={`alert alert-${docMsg.type} py-1 mt-1 mb-0`}>{docMsg.text}</div>}
                    </div>

                    {/* ── Affiliated College (next line) ── */}
                    <div className="col-sm-6">
                      <label className="form-label">Affiliated College Name</label>
                      <input className="form-control" value={colleges.find((c) => String(c.CollegeID) === String(form.CollegeID))?.CollegeDisplay || form.ACollege || ""} readOnly />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label">Affilated College Name</label>
                      <select
                        className="form-control"
                        name="CollegeID"
                        value={form.CollegeID}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Affilated College</option>
                        {colleges.map((college) => (
                          <option key={college.CollegeID} value={college.CollegeID}>{college.CollegeDisplay}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowModal(false);
                      setEditingTeacherId(null);
                      setForm(EMPTY_FORM);
                      setFormError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
